import { useState } from "react";
import { ShoppingBag, Plus, Minus, Trash2 } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";

export const CartSheet = ({ light }) => {
  const { items, setQty, removeItem, count, total } = useCart();
  const [open, setOpen] = useState(false);

  const checkout = () => {
    setOpen(false);
    setTimeout(() => {
      document.querySelector("#order")?.scrollIntoView({ behavior: "smooth" });
    }, 150);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="cart-button"
          aria-label="Open cart"
          className={`relative p-2 rounded-full transition-colors ${light ? "text-white hover:bg-white/10" : "text-ink hover:bg-ink/5"}`}
        >
          <ShoppingBag size={24} />
          {count > 0 && (
            <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-brand text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
              {count}
            </span>
          )}
        </button>
      </SheetTrigger>
      <SheetContent className="bg-cream w-full sm:max-w-md flex flex-col p-0" data-testid="cart-sheet">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-line">
          <SheetTitle className="font-serif text-2xl text-ink flex items-center gap-2">
            <ShoppingBag size={22} className="text-brand" /> Your Order
          </SheetTitle>
          <SheetDescription className="text-inkmuted text-sm">
            Review your items, then checkout to finalize your order.
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="cart-empty">
            <ShoppingBag size={44} className="text-line" />
            <p className="mt-4 text-inkmuted font-semibold">Your cart is empty</p>
            <p className="mt-1 text-sm text-inkmuted">Add dishes from the menu to get started.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {items.map((it) => (
              <div key={it.id} data-testid={`cart-item-${it.id}`} className="flex gap-3 items-center">
                <img src={it.image} alt={it.name} className="w-16 h-16 rounded-xl object-cover border border-line" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-ink text-sm leading-tight">{it.name}</p>
                  <p className="text-brand font-bold text-sm">${it.price.toFixed(2)}</p>
                  <div className="mt-1.5 inline-flex items-center gap-2 bg-white border border-line rounded-full px-1">
                    <button onClick={() => setQty(it.id, it.qty - 1)} data-testid={`cart-dec-${it.id}`} className="w-7 h-7 flex items-center justify-center text-ink hover:text-brand" aria-label="Decrease">
                      <Minus size={14} />
                    </button>
                    <span className="w-5 text-center text-sm font-bold" data-testid={`cart-qty-${it.id}`}>{it.qty}</span>
                    <button onClick={() => setQty(it.id, it.qty + 1)} data-testid={`cart-inc-${it.id}`} className="w-7 h-7 flex items-center justify-center text-ink hover:text-brand" aria-label="Increase">
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-ink text-sm">${(it.price * it.qty).toFixed(2)}</p>
                  <button onClick={() => removeItem(it.id)} data-testid={`cart-remove-${it.id}`} className="mt-2 text-inkmuted hover:text-brand" aria-label="Remove">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-line px-6 py-5 bg-white">
            <div className="flex items-center justify-between mb-4">
              <span className="font-semibold text-ink">Subtotal</span>
              <span data-testid="cart-total" className="font-serif text-2xl font-bold text-brand">${total.toFixed(2)}</span>
            </div>
            <button onClick={checkout} data-testid="cart-checkout" className="w-full rounded-full bg-brand text-white py-4 font-bold hover:bg-brand-hover transition-colors shadow-md">
              Checkout →
            </button>
            <p className="mt-3 text-center text-xs text-inkmuted">Please order at least 2 days in advance.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
